import { type UiStrings } from '@/constants/ui-locale'
import { ExclamationTriangleIcon, LogoutIcon } from '@/components/Icons'
import type { SharedRoomMember } from '@/pages/Meetings/utils/sharedRoomPresentation'
import {
  sharedRoomAvatarTone,
  sharedRoomInitials,
  sharedRoomParticipantKey,
} from '@/pages/Meetings/utils/sharedRoomInitials'

function formatLeaveModalMemberName(member: SharedRoomMember, t: UiStrings): string {
  if (member.role === 'host') {
    return `${member.name} (${t.sharedRoomMemberHost})`
  }
  return member.name
}

export default function SharedRoomLeaveConfirmModal({
  open,
  meetingTitle,
  members,
  t,
  leaving,
  onCancel,
  onConfirmLeave,
}: {
  open: boolean
  meetingTitle: string | null
  members: SharedRoomMember[]
  t: UiStrings
  leaving: boolean
  onCancel: () => void
  onConfirmLeave: () => void
}) {
  if (!open) return null

  const others = members.filter((m) => m.role !== 'you')

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shared-room-leave-title"
      data-testid="shared-room-leave-confirm-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-950/40 px-3 py-4 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label={t.cancel}
        className="fixed inset-0 cursor-default"
        onClick={onCancel}
      />

      <div className="animate-scale-in relative w-full max-w-[450px] rounded-2xl border border-slate-100 bg-white p-[25px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
        <div className="flex flex-col gap-3">
          <div className="flex items-start">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-rose-100 bg-rose-50 text-red-600 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-red-400">
              <LogoutIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 pl-3.5">
              <h2
                id="shared-room-leave-title"
                className="text-base font-bold leading-[22px] tracking-[-0.4px] text-slate-900 dark:text-[var(--text-primary)]"
              >
                {t.sharedRoomLeaveConfirmTitle}
              </h2>
              <p className="mt-1 text-xs leading-[18px] text-slate-500 dark:text-[var(--text-secondary)]">
                {meetingTitle ? (
                  <>
                    {t.sharedRoomLeaveConfirmBodyIntro}{' '}
                    <span className="font-bold text-slate-500 dark:text-[var(--text-secondary)]">
                      {meetingTitle}
                    </span>{' '}
                    {t.sharedRoomLeaveConfirmBodyOutro}
                  </>
                ) : (
                  t.sharedRoomLeaveConfirmBodyNoTitle
                )}
              </p>
            </div>
          </div>

          {others.length > 0 ? (
            <div className="flex items-center justify-between rounded-xl border border-slate-200/90 bg-[#fafbfc] px-[15px] pb-[11px] pt-[15px] dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.5px] text-slate-400">
                  {t.sharedRoomLeaveConfirmInRoom}
                </p>
                <p className="mt-0.5 text-xs leading-4 text-slate-700 dark:text-[var(--text-primary)]">
                  {others.map((m) => formatLeaveModalMemberName(m, t)).join(', ')}
                </p>
              </div>
              <div className="flex shrink-0 items-center pl-2">
                {others.slice(0, 4).map((member, index) => (
                  <span
                    key={sharedRoomParticipantKey(member.name, index)}
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ring-2 ring-white dark:ring-[var(--bg-secondary)] ${sharedRoomAvatarTone(member.name)} ${index > 0 ? '-ml-1' : ''}`}
                  >
                    {sharedRoomInitials(member.name)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex items-center rounded-xl border border-orange-100 bg-orange-50 px-[15px] py-[11px] dark:border-orange-900/40 dark:bg-orange-950/30">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-orange-800 dark:text-orange-200" />
            <p className="pl-2.5 text-[11.5px] leading-[14px] text-orange-900 dark:text-orange-100">
              {t.sharedRoomLeaveConfirmWarning}
            </p>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <button
              type="button"
              disabled={leaving}
              onClick={onCancel}
              className="rounded-lg border border-slate-300 bg-white px-[17px] py-[9px] text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--bg-tertiary)]"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              disabled={leaving}
              onClick={onConfirmLeave}
              className="rounded-lg bg-red-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              {t.sharedRoomLeave}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
